/**
 * Tenants — search, filter, manage tenant lifecycle
 */
import React, { useState, useCallback } from "react";
import { tenants as tenantsApi } from "../api.js";
import { useAsync, useDebounce } from "../hooks.js";

function lifecycleBadge(state: string) {
  const map: Record<string, string> = {
    live: "ops-badge-green", draft_only: "ops-badge-blue",
    trialing: "ops-badge-purple", suspended: "ops-badge-amber",
    cancelled: "ops-badge-red", archived: "ops-badge-neutral",
    provisioning: "ops-badge-blue",
  };
  return <span className={`ops-badge ${map[state] ?? "ops-badge-neutral"}`}>{state}</span>;
}

type ConfirmAction = { tenantId: string; tenantName: string; action: "suspend" | "reactivate" | "archive" | "delete" };

interface CreateTenantForm {
  email: string;
  password: string;
  ownerName: string;
  slug: string;
  businessName: string;
  planKey: string;
}

function deriveSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
}

export function TenantsView() {
  const [search, setSearch]     = useState("");
  const [state, setState]       = useState("");
  const [plan, setPlan]         = useState("");
  const [page, setPage]         = useState(1);
  const [confirm, setConfirm]   = useState<ConfirmAction | null>(null);
  const [reason, setReason]     = useState("");
  const [actionBusy, setBusy]   = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  // Create tenant modal state
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<CreateTenantForm>({
    email: "", password: "", ownerName: "", slug: "", businessName: "", planKey: "starter",
  });
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 350);

  const fetcher = useCallback(
    () => tenantsApi.list({ q: debouncedSearch, state, plan, page: String(page), pageSize: "50" }),
    [debouncedSearch, state, plan, page]
  );
  const { data, loading, error, reload } = useAsync<any>(fetcher, [debouncedSearch, state, plan, page]);

  const handleAction = async () => {
    if (!confirm) return;
    setBusy(true);
    setActionMsg(null);
    try {
      if (confirm.action === "suspend")    await tenantsApi.suspend(confirm.tenantId, reason);
      if (confirm.action === "reactivate") await tenantsApi.reactivate(confirm.tenantId);
      if (confirm.action === "archive")    await tenantsApi.archive(confirm.tenantId, reason);
      if (confirm.action === "delete")     await tenantsApi.delete(confirm.tenantId);
      setConfirm(null); setReason(""); reload();
    } catch (e: any) {
      setActionMsg(e.message);
    } finally { setBusy(false); }
  };

  const handleCreate = async () => {
    setCreateBusy(true);
    setCreateError(null);
    setCreateSuccess(null);
    try {
      const result = await tenantsApi.create(createForm);
      setCreateSuccess(`Tenant "${result.slug}" created. Login: ${createForm.email}`);
      setShowCreate(false);
      setCreateForm({ email: "", password: "", ownerName: "", slug: "", businessName: "", planKey: "starter" });
      reload();
    } catch (e: any) {
      setCreateError(e.message);
    } finally { setCreateBusy(false); }
  };

  // Auto-derive slug from business name
  const updateBusinessName = (name: string) => {
    setCreateForm((f) => ({
      ...f,
      businessName: name,
      slug: f.slug === "" || f.slug === deriveSlug(f.businessName)
        ? deriveSlug(name)
        : f.slug,
    }));
  };

  const rows: any[] = data?.items ?? [];

  return (
    <div className="ops-page">
      <div className="ops-page-header">
        <div>
          <h1 className="ops-page-title">Tenants</h1>
          {data && <p className="ops-page-subtitle">{data.total} total</p>}
        </div>
        <button className="ops-btn ops-btn-primary ops-btn-sm" onClick={() => setShowCreate(true)}>
          + Create Tenant
        </button>
      </div>

      {createSuccess && (
        <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid var(--ops-success)",
          borderRadius: "var(--ops-radius)", padding: "0.625rem 1rem", marginBottom: "1rem",
          color: "var(--ops-success)", fontSize: "0.8125rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>✓ {createSuccess}</span>
          <button style={{ background: "none", border: "none", color: "var(--ops-success)", cursor: "pointer" }}
            onClick={() => setCreateSuccess(null)}>✕</button>
        </div>
      )}

      {error && <div className="ops-error">{error}</div>}

      <div className="ops-filters">
        <input className="ops-input" placeholder="Search slug, name, email…"
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        <select className="ops-select" value={state} onChange={(e) => { setState(e.target.value); setPage(1); }}>
          <option value="">All states</option>
          {["live","draft_only","trialing","suspended","cancelled","archived"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select className="ops-select" value={plan} onChange={(e) => { setPlan(e.target.value); setPage(1); }}>
          <option value="">All plans</option>
          {["starter","professional","commerce_lite","internal_demo"].map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <button className="ops-btn ops-btn-secondary ops-btn-sm" onClick={reload}>↻ Refresh</button>
      </div>

      {loading && <div className="ops-loading">Loading tenants…</div>}

      {!loading && rows.length === 0 && <div className="ops-empty">No tenants found.</div>}

      {rows.length > 0 && (
        <div className="ops-table-wrap">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Slug</th><th>Business</th><th>Owner</th>
                <th>Plan</th><th>State</th><th>Subscription</th>
                <th>Published</th><th>Created</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <tr key={r.id}>
                  <td><code style={{ fontSize: "0.75rem" }}>{r.slug}</code></td>
                  <td>{r.businessName ?? <span style={{ color: "var(--ops-muted)" }}>—</span>}</td>
                  <td style={{ fontSize: "0.75rem", color: "var(--ops-muted)" }}>{r.ownerEmail ?? "—"}</td>
                  <td><span className="ops-badge ops-badge-purple">{r.planKey}</span></td>
                  <td>{lifecycleBadge(r.lifecycleState)}</td>
                  <td>{r.subscriptionStatus
                    ? <span className="ops-badge ops-badge-blue">{r.subscriptionStatus}</span>
                    : <span style={{ color: "var(--ops-muted)" }}>—</span>}
                  </td>
                  <td>{r.isPublished
                    ? <span className="ops-badge ops-badge-green">Live</span>
                    : <span className="ops-badge ops-badge-neutral">No</span>}
                  </td>
                  <td style={{ fontSize: "0.75rem", color: "var(--ops-muted)" }}>
                    {r.createdAt?.slice(0, 10)}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "0.375rem", flexWrap: "wrap" }}>
                      {r.lifecycleState !== "suspended" && r.lifecycleState !== "archived" && (
                        <button className="ops-btn ops-btn-sm ops-btn-secondary"
                          onClick={() => setConfirm({ tenantId: r.id, tenantName: r.businessName ?? r.slug, action: "suspend" })}>
                          Suspend
                        </button>
                      )}
                      {r.lifecycleState === "suspended" && (
                        <button className="ops-btn ops-btn-sm ops-btn-secondary"
                          onClick={() => setConfirm({ tenantId: r.id, tenantName: r.businessName ?? r.slug, action: "reactivate" })}>
                          Reactivate
                        </button>
                      )}
                      {r.lifecycleState !== "archived" && (
                        <button className="ops-btn ops-btn-sm ops-btn-secondary"
                          onClick={() => setConfirm({ tenantId: r.id, tenantName: r.businessName ?? r.slug, action: "archive" })}>
                          Archive
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.hasMore && (
        <div className="ops-pagination">
          {page > 1 && <button className="ops-btn ops-btn-sm ops-btn-secondary" onClick={() => setPage((p) => p - 1)}>← Prev</button>}
          <span>Page {page}</span>
          <button className="ops-btn ops-btn-sm ops-btn-secondary" onClick={() => setPage((p) => p + 1)}>Next →</button>
        </div>
      )}

      {/* Confirm modal */}
      {confirm && (
        <div className="ops-modal-overlay" onClick={() => setConfirm(null)}>
          <div className="ops-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="ops-modal-title">
              {confirm.action === "suspend"    && "Suspend tenant"}
              {confirm.action === "reactivate" && "Reactivate tenant"}
              {confirm.action === "archive"    && "Archive tenant"}
              {confirm.action === "delete"     && "Delete tenant"}
            </h2>
            <p style={{ color: "var(--ops-muted)", fontSize: "0.875rem", marginTop: 0 }}>
              <strong style={{ color: "var(--ops-text)" }}>{confirm.tenantName}</strong>
            </p>
            {(confirm.action === "suspend" || confirm.action === "archive") && (
              <div className="ops-form-group">
                <label className="ops-form-label">Reason (optional)</label>
                <input className="ops-input" value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason…" />
              </div>
            )}
            {actionMsg && <div className="ops-error">{actionMsg}</div>}
            <div className="ops-modal-actions">
              <button className="ops-btn ops-btn-secondary" onClick={() => setConfirm(null)}>Cancel</button>
              <button
                className={`ops-btn ${confirm.action === "delete" ? "ops-btn-danger" : "ops-btn-primary"}`}
                disabled={actionBusy}
                onClick={handleAction}
              >
                {actionBusy ? "Working…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Tenant modal */}
      {showCreate && (
        <div className="ops-modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="ops-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <h2 className="ops-modal-title">Create Tenant</h2>
            <p style={{ color: "var(--ops-muted)", fontSize: "0.8125rem", marginTop: 0, marginBottom: "1rem" }}>
              Creates a user account, tenant, trial subscription, and empty draft — ready for the tenant dashboard.
            </p>

            <div className="ops-form-group">
              <label className="ops-form-label">Business Name *</label>
              <input className="ops-input" value={createForm.businessName}
                onChange={(e) => updateBusinessName(e.target.value)}
                placeholder="Sharma & Co Chartered Accountants" />
            </div>

            <div className="ops-form-group">
              <label className="ops-form-label">Slug * (URL path: live.bizzness.in/<strong>{createForm.slug || "..."}</strong>)</label>
              <input className="ops-input" value={createForm.slug}
                onChange={(e) => setCreateForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
                placeholder="sharma-ca" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <div className="ops-form-group">
                <label className="ops-form-label">Owner Email *</label>
                <input className="ops-input" type="email" value={createForm.email}
                  onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="owner@example.com" />
              </div>
              <div className="ops-form-group">
                <label className="ops-form-label">Password *</label>
                <input className="ops-input" type="text" value={createForm.password}
                  onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="initial password" />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <div className="ops-form-group">
                <label className="ops-form-label">Owner Name</label>
                <input className="ops-input" value={createForm.ownerName}
                  onChange={(e) => setCreateForm((f) => ({ ...f, ownerName: e.target.value }))}
                  placeholder="Full name (optional)" />
              </div>
              <div className="ops-form-group">
                <label className="ops-form-label">Plan</label>
                <select className="ops-select" style={{ width: "100%" }} value={createForm.planKey}
                  onChange={(e) => setCreateForm((f) => ({ ...f, planKey: e.target.value }))}>
                  <option value="starter">Starter (14-day trial)</option>
                  <option value="professional">Professional (14-day trial)</option>
                  <option value="commerce_lite">Commerce Lite (7-day trial)</option>
                  <option value="internal_demo">Internal Demo (free)</option>
                </select>
              </div>
            </div>

            {createError && <div className="ops-error">{createError}</div>}

            <div className="ops-modal-actions">
              <button className="ops-btn ops-btn-secondary" onClick={() => { setShowCreate(false); setCreateError(null); }}>
                Cancel
              </button>
              <button className="ops-btn ops-btn-primary" disabled={createBusy} onClick={handleCreate}>
                {createBusy ? "Creating…" : "Create Tenant"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
